<template>
<div class="leiten">

    <!-- 1. Sitzung eroeffnen -->
    <form v-if="!sitzung" @submit.prevent="eroeffnen" class="formular">
        <p class="pfad">Unterricht</p>
        <h1>Sitzung leiten</h1>
        <p class="hinweis">
            Wähle ein Thema. Du bekommst einen Code für die Klasse. Jede und jeder
            arbeitet im eigenen Tempo, du siehst hier den Stand.
        </p>

        <fieldset>
            <legend>Thema</legend>
            <label v-for="t in themen" :key="t.id" class="wahl">
                <input type="radio" name="thema" :value="t.id" v-model="themaId" />
                {{ t.label }}
            </label>
        </fieldset>

        <fieldset v-if="thema.kategorien.length > 0">
            <legend>Kategorien <span class="leise">(keine gewählt = alle)</span></legend>
            <label v-for="k in thema.kategorien" :key="k" class="wahl">
                <input type="checkbox" :value="k" v-model="kategorien" />
                {{ k }}
            </label>
        </fieldset>

        <fieldset v-if="themaId === 'sprechregeln'">
            <legend>Übungsarten <span class="leise">(keine gewählt = alle)</span></legend>
            <label v-for="a in uebungsarten" :key="a.id" class="wahl">
                <input type="checkbox" :value="a.id" v-model="arten" />
                {{ a.name }}
            </label>
        </fieldset>

        <!-- Ablenker gibt es nur beim Sortieren; die Fehlersuche baut ihre
             Fehler ohnehin aus den Ablenkern. -->
        <fieldset v-if="themaId === 'sprechregeln' && mitSortieren">
            <legend>Schwierigkeit</legend>
            <label class="wahl">
                <input type="checkbox" v-model="ablenker" />
                Mit Ablenkern <span class="leise">(bei «Alles sortieren»)</span>
            </label>
        </fieldset>

        <fieldset v-if="themaId === 'sprechregeln' && mitReagieren">
            <legend>Rolle bei «Reagieren»</legend>
            <label class="wahl"><input type="radio" value="zufall" v-model="rolle" /> zufällig</label>
            <label class="wahl"><input type="radio" value="rufer" v-model="rolle" /> rufende Station</label>
            <label class="wahl"><input type="radio" value="gegenstation" v-model="rolle" /> Gegenstation</label>
        </fieldset>

        <label class="feld">
            Aufgaben pro Person <span class="leise">(höchstens {{ maxAnzahl }})</span>
            <input type="number" v-model.number="anzahl" min="1" :max="maxAnzahl" required />
        </label>

        <label class="feld">
            Instruktorenpasswort
            <input type="password" v-model="passwort" autocomplete="current-password" required />
        </label>

        <p v-if="artOhneAblauf" class="fehler" role="alert">
            «{{ artOhneAblauf.name }}» geht mit keiner der gewählten Kategorien.
            Wähle eine Kategorie mit mehreren Stationen dazu.
        </p>

        <p v-if="fehler" class="fehler" role="alert">{{ fehler }}</p>

        <button type="submit" class="haupt" :disabled="laedt || artOhneAblauf">Sitzung eröffnen</button>
    </form>

    <!-- 2. Laufende Sitzung -->
    <div v-else class="laufend">
        <p class="pfad">Unterricht · Sitzung läuft</p>
        <div class="kopf">
            <div>
                <p class="leise">Code für die Klasse</p>
                <p class="code">{{ sitzung.code }}</p>
            </div>
            <div v-if="uebersicht" class="eckdaten">
                <p>
                    {{ themaLabel }} · {{ uebersicht.konfiguration.anzahl }} Aufgaben pro Person<template
                        v-if="uebersicht.konfiguration.ablenker"> · mit Ablenkern</template>
                </p>
                <p v-if="uebersicht.konfiguration.kategorien.length > 0" class="leise">
                    Kategorien: {{ uebersicht.konfiguration.kategorien.join(', ') }}
                </p>
                <p v-if="(uebersicht.konfiguration.uebungsarten ?? []).length > 0" class="leise">
                    Übungsarten: {{ uebersicht.konfiguration.uebungsarten.map(a => artNamen[a] ?? a).join(', ') }}
                </p>
                <p>{{ uebersicht.schueler.length }} beigetreten</p>
            </div>
        </div>

        <!-- Steuerung: Beitritt und Ende -->
        <div v-if="uebersicht" class="steuerung">
            <p class="beitritt" :class="{ zu: !uebersicht.beitrittOffen }">
                {{ uebersicht.beitrittOffen ? 'Beitritt offen' : 'Beitritt geschlossen' }}
            </p>
            <button @click="beitrittUmschalten" :disabled="laedt">
                {{ uebersicht.beitrittOffen ? 'Beitritt schliessen' : 'Beitritt wieder öffnen' }}
            </button>

            <button v-if="!beendenFragen" class="rechts" @click="beendenFragen = true">Sitzung beenden</button>
            <div v-else class="rechts bestaetigen" role="group" aria-label="Sitzung beenden bestätigen">
                <span>Damit löschst du alle Ergebnisse dieser Sitzung.</span>
                <button class="gefahr" @click="beendenBestaetigt" :disabled="laedt">Ja, beenden</button>
                <button @click="beendenFragen = false">Abbrechen</button>
            </div>
        </div>

        <p v-if="verbindungWeg" class="fehler" role="status">
            Keine Verbindung zum Server. Der Stand ist vielleicht nicht aktuell.
        </p>
        <!-- Nie verbunden (z.B. alte Sitzung im Browser, Server weg): ohne
             diesen Knopf gaebe es keinen Weg zurueck zum Formular. -->
        <button v-if="verbindungWeg && !uebersicht" class="zurueck-zum-formular" @click="vergessen">Neue Sitzung eröffnen</button>

        <template v-if="uebersicht && uebersicht.schueler.length > 0">
            <h2>Die Klasse</h2>
            <div class="tabelle-rahmen">
                <table>
                    <thead>
                        <tr>
                            <th scope="col">Kategorie</th>
                            <th v-for="s in klassenSpalten" :key="s.id" scope="col">{{ s.label }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="z in klasse" :key="z.kategorie">
                            <th scope="row">{{ z.kategorie }}</th>
                            <td v-for="s in klassenSpalten" :key="s.id" :class="{ null: z.anzahl[s.id] === 0 }">
                                {{ z.anzahl[s.id] }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>

            <template v-if="mitReihenfolge">
                <h2>Nach Übungsart</h2>
                <div class="tabelle-rahmen">
                    <table>
                        <thead>
                            <tr>
                                <th scope="col">Übungsart</th>
                                <th v-for="s in klassenSpalten" :key="s.id" scope="col">{{ s.label }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="z in klasseNachArt" :key="z.kategorie">
                                <th scope="row">{{ z.kategorie }}</th>
                                <td v-for="s in klassenSpalten" :key="s.id" :class="{ null: z.anzahl[s.id] === 0 }">
                                    {{ z.anzahl[s.id] }}
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>

            <h2>Pro Person</h2>
            <div class="tabelle-rahmen">
                <table>
                    <thead>
                        <tr>
                            <th scope="col">Name</th>
                            <th scope="col">Stand</th>
                            <template v-if="mitReihenfolge">
                                <th scope="col">Gerade bei</th>
                                <th scope="col">Übersprungen</th>
                            </template>
                            <th scope="col">Zuletzt aktiv</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="p in personen" :key="p.name">
                            <th scope="row">{{ p.name }}</th>
                            <td>
                                {{ p.fortschritt.bearbeitet }} von {{ p.fortschritt.gesamt }}
                                <span v-if="p.fortschritt.fertig" class="fertig">fertig</span>
                            </td>
                            <template v-if="mitReihenfolge">
                                <td>{{ p.fortschritt.aktuell ? `Aufgabe ${p.fortschritt.aktuell}` : '—' }}</td>
                                <td>{{ p.fortschritt.uebersprungen }}</td>
                            </template>
                            <td>{{ p.zuletzt }}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>

        <p v-else-if="uebersicht" class="hinweis">
            Noch niemand beigetreten.
            <template v-if="uebersicht.beitrittOffen">Schreib den Code an die Wandtafel.</template>
        </p>
    </div>

</div>
</template>

<script>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { sitzungThemen, verfuegbareAnzahl } from '@/data/SitzungThemen'
import { uebungsarten, passt } from '@/data/Uebungsarten'
import { schemata } from '@/data/daten'
import { sitzungEroeffnen, uebersichtLaden, beitrittSetzen, sitzungBeenden } from '@/logic/sitzungApi'
import { spalten, proKategorie, proUebungsart, fortschritt, vorWieLange } from '@/logic/sitzungAuswertung'

// id der Uebungsart -> Name, so wie im Menue.
const artNamen = Object.fromEntries(uebungsarten.map(a => [a.id, a.name]))

// Code und Lehrerschluessel ueberleben ein Neuladen der Seite.
const SPEICHER = 'funktrainer.lehrer'
const INTERVALL_MS = 3000

function lesen() {
    try {
        return JSON.parse(localStorage.getItem(SPEICHER))
    } catch {
        return null
    }
}

function schreiben(wert) {
    try {
        if (wert) localStorage.setItem(SPEICHER, JSON.stringify(wert))
        else localStorage.removeItem(SPEICHER)
    } catch {
        // Kein Speicher (z.B. privates Fenster): die Sitzung laeuft trotzdem,
        // nur ein Neuladen verliert sie.
    }
}

export default {
    setup() {
        // --- Formular ---
        const themen = sitzungThemen
        const themaId = ref('funk')
        const kategorien = ref([])
        const anzahl = ref(5)
        const ablenker = ref(false)
        const arten = ref([])
        const rolle = ref('zufall')
        const passwort = ref('')
        const fehler = ref('')
        const laedt = ref(false)

        const thema = computed(() => themen.find(t => t.id === themaId.value))
        const maxAnzahl = computed(() => verfuegbareAnzahl(thema.value, kategorien.value))
        // Keine Art gewaehlt = alle, also auch "Alles sortieren".
        const mitSortieren = computed(() => arten.value.length === 0 || arten.value.includes('allesSortieren'))
        const mitReagieren = computed(() => arten.value.length === 0 || arten.value.includes('reagieren'))
        // Eine gewaehlte Uebungsart, zu der keine gewaehlte Kategorie passt
        // (z.B. nur "Grundschema" mit "Einfach sortieren") — dann gibt es
        // nichts zu ziehen. Keine Art gewaehlt = alle, die gehen: kein Problem.
        const artOhneAblauf = computed(() => {
            if (themaId.value !== 'sprechregeln') return null
            const ablaeufe = kategorien.value.length === 0
                ? schemata
                : schemata.filter(s => kategorien.value.includes(s.name))
            return uebungsarten.find(a => arten.value.includes(a.id) && !ablaeufe.some(s => passt(a.id, s))) ?? null
        })

        // Anderes Thema: alte Kategorien gelten nicht mehr.
        watch(themaId, () => { kategorien.value = [] })
        // Weniger Aufgaben verfuegbar als gewaehlt: Zahl herunterziehen.
        watch(maxAnzahl, (max) => {
            if (anzahl.value > max) anzahl.value = max
        })

        // --- Laufende Sitzung ---
        const sitzung = ref(lesen())
        const uebersicht = ref(null)
        const verbindungWeg = ref(false)
        const jetzt = ref(Date.now())
        let zeitgeber = null

        const laden = async () => {
            const { daten, status } = await uebersichtLaden(sitzung.value.code, sitzung.value.lehrerSchluessel)
            jetzt.value = Date.now()
            if (daten) {
                uebersicht.value = daten
                verbindungWeg.value = false
            } else if (status === 401) {
                // Sitzung gibt es nicht mehr (Server neu gestartet oder abgelaufen).
                vergessen()
                fehler.value = 'Die Sitzung gibt es nicht mehr. Eröffne eine neue.'
            } else {
                verbindungWeg.value = true
            }
        }

        const starten = () => {
            laden()
            zeitgeber = setInterval(laden, INTERVALL_MS)
        }

        // Nur hier im Browser: Sitzung loslassen und zurueck zum Formular.
        const vergessen = () => {
            clearInterval(zeitgeber)
            zeitgeber = null
            schreiben(null)
            sitzung.value = null
            uebersicht.value = null
            beendenFragen.value = false
        }

        const beitrittUmschalten = async () => {
            laedt.value = true
            const offen = !uebersicht.value.beitrittOffen
            const { daten } = await beitrittSetzen(sitzung.value.code, sitzung.value.lehrerSchluessel, offen)
            laedt.value = false
            if (daten) uebersicht.value.beitrittOffen = daten.beitrittOffen
            else verbindungWeg.value = true
        }

        const beendenFragen = ref(false)

        const beendenBestaetigt = async () => {
            laedt.value = true
            const { status } = await sitzungBeenden(sitzung.value.code, sitzung.value.lehrerSchluessel)
            laedt.value = false
            // 204 = beendet; 401 = gab es schon nicht mehr. Beides heisst: vorbei.
            if (status === undefined || status === 401) vergessen()
            else verbindungWeg.value = true
        }

        const eroeffnen = async () => {
            fehler.value = ''
            laedt.value = true
            const konfiguration = {
                thema: themaId.value,
                kategorien: kategorien.value,
                anzahl: anzahl.value,
                ablenker: themaId.value === 'sprechregeln' && mitSortieren.value && ablenker.value,
                uebungsarten: themaId.value === 'sprechregeln' ? arten.value : [],
                rolle: rolle.value
            }
            const { daten, status } = await sitzungEroeffnen(passwort.value, konfiguration)
            laedt.value = false
            if (!daten) {
                fehler.value = status === 401 ? 'Das Passwort stimmt nicht.'
                    : status === 429 ? 'Zu viele falsche Versuche. Warte eine Minute.'
                    : status === 0 ? 'Der Server ist nicht erreichbar.'
                    : 'Die Sitzung konnte nicht eröffnet werden.'
                return
            }
            passwort.value = ''
            sitzung.value = { code: daten.code, lehrerSchluessel: daten.lehrerSchluessel }
            schreiben(sitzung.value)
            starten()
        }

        onMounted(() => {
            if (sitzung.value) starten()
        })
        onUnmounted(() => clearInterval(zeitgeber))

        const themaLabel = computed(() =>
            themen.find(t => t.id === uebersicht.value.konfiguration.thema).label)
        const klassenSpalten = computed(() => spalten[uebersicht.value.konfiguration.thema])
        const klasse = computed(() => proKategorie(uebersicht.value))
        const klasseNachArt = computed(() => proUebungsart(uebersicht.value, artNamen))
        // Nur Sprechregeln kommen der Reihe nach; Funkaufgaben sind eine freie Liste.
        const mitReihenfolge = computed(() => uebersicht.value.konfiguration.thema === 'sprechregeln')
        const personen = computed(() => uebersicht.value.schueler.map(s => ({
            name: s.name,
            fortschritt: fortschritt(s, uebersicht.value.konfiguration),
            zuletzt: vorWieLange(s.letzteAktivitaet, jetzt.value)
        })))

        return {
            themen, themaId, thema, kategorien, anzahl, ablenker, uebungsarten, arten, mitSortieren, mitReagieren, rolle, artOhneAblauf, artNamen, maxAnzahl, passwort, fehler, laedt, eroeffnen,
            sitzung, uebersicht, verbindungWeg, themaLabel, klassenSpalten, klasse, klasseNachArt, personen, mitReihenfolge,
            beitrittUmschalten, beendenFragen, beendenBestaetigt, vergessen
        }
    }
}
</script>

<style scoped>
.leiten {
    max-width: 1000px;
    padding: 2.25rem 2.5rem 3rem;
    text-align: left;
    color: var(--zs-tinte);
}

.pfad {
    margin: 0 0 0.35rem;
    font-size: 0.75rem;
    font-weight: 650;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

h1 {
    margin: 0 0 0.5rem;
    font-size: 2rem;
    font-weight: 750;
    letter-spacing: -0.02em;
    color: var(--zs-tinte);
}

h2 {
    margin: 2rem 0 0.75rem;
    font-size: 0.8rem;
    font-weight: 650;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--zs-grau);
}

.hinweis {
    margin: 0 0 1.5rem;
    max-width: 60ch;
    line-height: 1.5;
    color: var(--zs-grau);
}

.leise {
    color: var(--zs-grau);
    font-weight: 400;
}

/* --- Formular: eine Tafel, wie die Arbeitsflaeche der Uebungen --- */
.formular {
    max-width: 620px;
    padding: 1.75rem 2rem 2rem;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke-gross);
    box-shadow: var(--zs-schatten);
}

fieldset {
    margin: 0 0 1.25rem;
    padding: 0;
    border: 0;
}

legend {
    margin-bottom: 0.5rem;
    font-weight: 600;
}

.wahl {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    margin: 0 1rem 0.4rem 0;
    cursor: pointer;
}

.feld {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    margin: 0 0 1.25rem;
    font-weight: 600;
}

.feld input {
    max-width: 280px;
    padding: 0.55rem 0.7rem;
    font: inherit;
    font-weight: 400;
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    background: var(--zs-flaeche);
}

.feld input:focus-visible,
.wahl input:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

/* Hauptaktion blau, wie ueberall in der App. */
.haupt {
    min-height: 2.75rem;
    padding: 0.6rem 1.4rem;
    font: inherit;
    font-weight: 600;
    color: #ffffff;
    background: var(--zs-blau);
    border: 0;
    border-radius: var(--zs-ecke);
    cursor: pointer;
}

.haupt:hover {
    background: var(--zs-blau-dunkel);
}

.haupt:disabled {
    opacity: 0.6;
    cursor: wait;
}

.fehler {
    margin: 0 0 1rem;
    padding: 0.6rem 0.8rem;
    border: 1px solid rgba(179, 38, 30, 0.3);
    border-radius: var(--zs-ecke);
    background: var(--zs-warnrot-hell);
    color: var(--zs-warnrot);
}

/* --- Laufende Sitzung --- */
.kopf {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 1rem 2rem;
    padding: 1.5rem 1.75rem;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke-gross);
    box-shadow: var(--zs-schatten);
}

.kopf p {
    margin: 0;
}

.code {
    font-size: 3rem;
    font-weight: 700;
    letter-spacing: 0.15em;
    color: var(--zs-blau);
    font-variant-numeric: tabular-nums;
}

.eckdaten {
    text-align: right;
    line-height: 1.5;
}

.steuerung {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.75rem;
    margin-top: 1rem;
}

.steuerung p {
    margin: 0;
}

.beitritt {
    font-weight: 600;
    color: var(--zs-oliv);
}

.beitritt.zu {
    color: var(--zs-grau);
}

.steuerung button {
    padding: 0.5rem 1rem;
    font: inherit;
    color: var(--zs-tinte);
    background: #ffffff;
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    cursor: pointer;
}

.steuerung button:hover {
    border-color: var(--zs-tinte);
}

.steuerung button:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}

.steuerung .rechts {
    margin-left: auto;
}

.bestaetigen {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
    color: var(--zs-warnrot);
}

.steuerung .gefahr {
    color: #ffffff;
    background: var(--zs-warnrot);
    border-color: var(--zs-warnrot);
}

/* Tabellen auf einer Tafel, scrollen bei Bedarf seitlich in sich. */
.tabelle-rahmen {
    overflow-x: auto;
    background: var(--zs-tafel);
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke-gross);
    box-shadow: var(--zs-schatten);
}

tbody tr:last-child th,
tbody tr:last-child td {
    border-bottom: none;
}

table {
    width: 100%;
    border-collapse: collapse;
    font-variant-numeric: tabular-nums;
}

th,
td {
    padding: 0.65rem 1rem;
    border-bottom: 1px solid var(--zs-rahmen);
    text-align: left;
    white-space: nowrap;
}

thead th {
    font-size: 0.78rem;
    font-weight: 650;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--zs-grau);
    background: var(--zs-flaeche);
}

tbody th {
    font-weight: 600;
}

td.null {
    color: var(--zs-grau);
}

.fertig {
    margin-left: 0.5rem;
    padding: 0.1rem 0.45rem;
    font-size: 0.8rem;
    border-radius: var(--zs-ecke);
    background: var(--zs-oliv-hell);
    color: var(--zs-oliv);
}

@media (max-width: 600px) {
    .eckdaten {
        text-align: left;
    }
}

@media (max-width: 900px) {
    .leiten {
        padding: 1.5rem 16px 2.5rem;
    }

    .formular {
        padding: 1.25rem;
    }
}

.zurueck-zum-formular {
    margin-top: 0.75rem;
    padding: 0.6rem 1.1rem;
    font: inherit;
    color: var(--zs-tinte);
    background: #ffffff;
    border: 1px solid var(--zs-rahmen);
    border-radius: var(--zs-ecke);
    cursor: pointer;
}

.zurueck-zum-formular:focus-visible {
    outline: 3px solid var(--zs-orange);
    outline-offset: 2px;
}
</style>
